/* eslint-disable react-refresh/only-export-components -- co-located options+helpers; HMR-only warning, no runtime impact */
import { Filter, ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Seg } from '@/components/ui/seg';
import { cn } from '@/lib/utils';

export type PlayerType = 'all' | 'batters' | 'pitchers';

export const POSITION_GROUPS: { value: string; label: string }[] = [
  { value: 'All', label: 'All Positions' },
  { value: 'Hitters', label: 'Hitters' },
  { value: 'Pitchers', label: 'Pitchers' },
  { value: 'ProbablePitchers', label: 'Probable Pitchers' },
  { value: 'TwoStart', label: '2-Start SP' },
  { value: 'C', label: 'C' },
  { value: '1B', label: '1B' },
  { value: '2B', label: '2B' },
  { value: '3B', label: '3B' },
  { value: 'SS', label: 'SS' },
  { value: 'OF', label: 'OF' },
  { value: 'DH', label: 'DH' },
  { value: 'SP', label: 'SP' },
  { value: 'RP', label: 'RP' },
];

const TRIGGER_LABEL_OVERRIDES: Record<string, string> = {
  All: 'All Pos',
  ProbablePitchers: 'Prob. SP',
};

export function getPositionTriggerLabel(value: string): string {
  if (TRIGGER_LABEL_OVERRIDES[value]) return TRIGGER_LABEL_OVERRIDES[value];
  return POSITION_GROUPS.find((g) => g.value === value)?.label ?? value;
}

const PLAYER_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'batters', label: 'Batters' },
  { value: 'pitchers', label: 'Pitchers' },
] as const;

interface PositionPriorityRowProps {
  playerType: PlayerType;
  onPlayerTypeChange: (value: PlayerType) => void;
  posFilter: string;
  onPosFilterChange: (value: string) => void;
}

export function PositionPriorityRow({
  playerType,
  onPlayerTypeChange,
  posFilter,
  onPosFilterChange,
}: PositionPriorityRowProps) {
  const posActive = posFilter !== 'All';

  return (
    <div className="flex gap-2 items-stretch w-full min-w-0">
      <Seg
        className="flex-[1.6] min-w-0"
        value={playerType}
        options={PLAYER_TYPE_OPTIONS}
        onChange={(v) => onPlayerTypeChange(v as PlayerType)}
        ariaLabel="Player type"
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Position filter"
            className={cn(
              'flex items-center justify-center gap-1.5 px-3 h-[34px] rounded-full',
              'bg-transparent border text-[12px] font-semibold whitespace-nowrap shrink-0',
              'transition-[box-shadow,color] duration-150',
              'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[hsl(var(--ring))]',
              posActive
                ? 'text-primary border-primary'
                : 'text-muted-foreground border-border',
            )}
          >
            <Filter className="w-3.5 h-3.5" />
            <span>{getPositionTriggerLabel(posFilter)}</span>
            <ChevronDown className="w-3.5 h-3.5 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
          {POSITION_GROUPS.map((g) => (
            <DropdownMenuItem
              key={g.value}
              onClick={() => onPosFilterChange(g.value)}
              className={cn(g.value === posFilter && 'text-primary font-semibold')}
            >
              {g.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default PositionPriorityRow;
